// ============================================================================
// LE PANNEAU « MODIFICATION DE MASSE » DES PRIX — la maquette `pgMass`, transposée.
//
// « Rien n'est écrit avant que vous validiez l'aperçu » : la phrase de la maquette est VRAIE par
// construction — l'aperçu et l'écriture partagent LA MÊME fonction côté serveur, l'écran ne calcule
// aucun prix (il ne saurait pas le faire à l'identique, et deux calculs finiraient par diverger).
//
// ⚠️ L'APERÇU EST SERVEUR, PAS LOCAL. La maquette transforme en mémoire parce qu'elle tient tout le
// catalogue ; ici la portée peut couvrir des milliers de déclinaisons, avec des dérogations par
// déclinaison qu'une transformation « par modèle » aplatirait. Le serveur rend des lignes par MODÈLE
// (bornes avant/après) et des totaux mesurés — jamais des chiffres plausibles.
// ============================================================================
import { useEffect, useMemo, useState } from 'react';
import { confirm, Icon, Modal, Picker, showToast, Trans } from '@orgazone/ui-web';
import { currentCurrency, formatMinor, formatMoney, parseMinor } from '@orgazone/money-format';
import { api, type ArticleFacet, type MassChange, type MassPreview, type MassPreviewRow, type MassRounding } from '../api';
import { indicateursPrix } from './prix-derive';
import { facetLabel } from '../vocabulary';
import { key, t, type Translatable } from '../i18n';

/**
 * Les cibles offertes, dans l'ordre des pastilles — la MARQUE d'abord : le fournisseur est un GROUPE
 * générique de marques, la marque est ce que portent les articles (la maquette listait le fournisseur
 * en premier — divergence statuée). Cibler le groupe reste offert : c'est toutes ses marques d'un coup.
 */
export const MASS_TARGETS = ['marq', 'fam', 'sais', 'sup'] as const satisfies readonly ArticleFacet[];
export type MassTarget = (typeof MASS_TARGETS)[number];

/**
 * Le mot d'une cible — celui du MAGASIN, lu au RENDU.
 * ⚠️ UNE FONCTION, PAS UNE TABLE DE MODULE : une chaîne posée au chargement du fichier figerait
 *    « Saison » avant que le contexte magasin ne soit lu.
 */
export function massTargetLabel(target: MassTarget): string {
  // Le fournisseur est un GROUPE de marques pour le ciblage : le mot du magasin, qualifié.
  return target === 'sup' ? t('{v} (groupe)', { v: facetLabel('sup') }) : facetLabel(target);
}

/**
 * Les arrondis de la maquette, libellé + exemple — les CLÉS sont celles du serveur.
 * ⚠️ L'EXEMPLE EST UN MONTANT, rendu dans la devise du magasin : « 69,99 € » écrit en dur mentait sous un magasin en
 *    francs. Seul « Aucun » s'explique par une phrase.
 */
const ARRONDIS: { k: MassRounding; l: Translatable; exemple: number | Translatable }[] = [
  { k: 'non', l: key('Aucun'), exemple: key('Prix exact') },
  { k: '99', l: ',99', exemple: 6999 },
  { k: '95', l: ',95', exemple: 6995 },
  { k: '90', l: ',90', exemple: 6990 },
  { k: 'ent', l: key('Entier'), exemple: 7000 },
  { k: 'dem', l: '0,50', exemple: 6950 },
];

const euro = (cents: number) => formatMoney(cents, { thousandsSep: ' ' });
/** Une borne ou une plage : « 69,99 € » quand min = max, « 69,99 € – 79,99 € » sinon. */
const plage = (min: number, max: number) => (min === max ? euro(min) : `${euro(min)} – ${euro(max)}`);

/** La marge APRÈS d'une ligne d'aperçu — dérivée par prix-derive, uniquement quand elle est fondée. */
function margeApres(r: MassPreviewRow): string {
  if (r.corner || r.vatRateBp == null || r.costMinFine == null || r.nextMinCents !== r.nextMaxCents) return '—';
  const ind = indicateursPrix(r.nextMinCents, r.costMinFine, r.vatRateBp);
  return ind.margeBp == null ? 'N/C' : `${formatMinor(ind.margeBp, { scale: 2, displayScale: 1 })} %`;
}

export function PanneauMassePrix({ open, onClose, onApplied }: {
  open: boolean;
  onClose: () => void;
  /** L'écriture a abouti : la grille derrière doit se relire. */
  onApplied: () => void;
}) {
  const [target, setCible] = useState<ArticleFacet>('marq');
  const [valeur, setValeur] = useState('');
  const [mode, setMode] = useState<'pct' | 'eur'>('pct');
  /** La variation, FRAPPE BRUTE — négative pour baisser, comme l'annonce le champ. */
  const [saisie, setSaisie] = useState('10');
  const [arrondi, setArrondi] = useState<MassRounding>('99');
  const [apercu, setApercu] = useState<MassPreview | null>(null);
  /** L'instant où l'aperçu affiché a été mesuré — c'est LUI que la garde `asOf` de l'écriture reçoit. */
  const [apercuAt, setApercuAt] = useState(0);
  const [failure, setFailure] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [ecriture, setEcriture] = useState(false);

  // La variation parsée — même lecture que partout (parseMinor), en centièmes : % à 2 décimales
  // devient des points de base, € devient des centimes.
  const change: MassChange | null = useMemo(() => {
    const lu = parseMinor(saisie, { scale: 2 });
    if (lu.state !== 'ok' || lu.minor === 0) return null;
    return { mode, value: lu.minor };
  }, [saisie, mode]);

  const filters = useMemo(() => (valeur ? { [target]: valeur } : null), [target, valeur]);

  useEffect(() => {
    if (!open || !filters || !change) { setApercu(null); return; }
    let vivant = true;
    setEnCours(true);
    // ⚠️ Un réglage = une requête serait une requête par frappe : on laisse retomber.
    const timer = setTimeout(() => {
      api.massPricePreview({ filters, change, rounding: arrondi })
        .then((r) => { if (vivant) { setApercu(r); setApercuAt(Date.now()); setFailure(null); } })
        .catch((e: unknown) => { if (vivant) { setApercu(null); setFailure(e instanceof Error ? e.message : String(e)); } })
        .finally(() => { if (vivant) setEnCours(false); });
    }, 350);
    return () => { vivant = false; clearTimeout(timer); };
  }, [open, filters, change, arrondi]);

  async function appliquer() {
    if (!filters || !change || !apercu) return;
    const ok = await confirm({
      title: t('Modifier {n} prix ?', { n: apercu.totals.changed }),
      // ⚠️ Les deux phrases sont VRAIES par mécanique : le tout-ou-rien est celui de la transaction,
      //    et la parenthèse de verrou est documentée sur la route (même règle que /articles/scope).
      message: t('{declinaisons} sur {modeles} recevront leur nouveau prix — tout ou rien, en une transaction. Les caisses de ce magasin peuvent être bloquées quelques secondes pendant l’écriture, et recevront les prix au prochain contact.', {
        declinaisons: t('{n} déclinaison', { n: apercu.totals.changed }),
        modeles: t('{n} modèle', { n: apercu.totals.models }),
      }),
      confirmLabel: t('Appliquer'),
    });
    if (!ok) return;
    setEcriture(true);
    try {
      const r = await api.massPriceApply({
        scope: { filters, excluded: [], asOf: apercuAt },
        change, rounding: arrondi,
        idempotencyKey: `mass-price-${crypto.randomUUID()}`,
      });
      const details = [
        t('{n} prix écrit', { n: r.written }),
        r.unchanged > 0 ? t('{n} inchangé', { n: r.unchanged }) : null,
        // ⚠️ Les écartés se DISENT : ce sont des modèles qui ont bougé depuis l'aperçu — ils n'ont
        //    pas été touchés, et celui qui les voulait doit relancer.
        r.drifted > 0 ? t('{n} modèle écarté (modifié entre-temps — relancez pour les couvrir)', { n: r.drifted }) : null,
      ].filter(Boolean).join(' · ');
      showToast(details, r.drifted > 0 ? 'warn' : 'ok');
      onApplied();
      onClose();
    } catch (e) {
      showToast(e instanceof Error ? e.message : String(e), 'err');
    } finally {
      setEcriture(false);
    }
  }

  const segment = (actif: boolean): React.CSSProperties => ({
    height: 32, padding: '0 13px', border: 'none', borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
    background: actif ? 'var(--paper)' : 'transparent',
    color: actif ? 'var(--teal-800)' : 'var(--ink-500)',
    fontSize: 'var(--t-body)', fontWeight: actif ? 700 : 500,
    boxShadow: actif ? '0 1px 3px rgba(15,20,25,.16)' : 'none',
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('Modification de masse')}
      subtitle={t('Rien n’est écrit avant que vous validiez l’aperçu')}
      icon="percent"
      width="lg"
      footer={(
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={ecriture}>{t('Fermer')}</button>
          {/* ⚠️ UN APERÇU EN COURS DE CALCUL NE S'APPLIQUE PAS : l'ancien reste affiché pendant que le nouveau se calcule,
              et appliquer écrirait les réglages DU MOMENT — des prix que l'écran ne montre pas, sous une confirmation qui
              compte ceux de l'ancien aperçu. */}
          <button
            type="button" className="btn-cta"
            disabled={ecriture || enCours || !apercu || apercu.totals.changed === 0}
            title={!apercu ? t('Choisissez une cible et une variation pour voir l’aperçu.') : enCours ? t('L’aperçu se recalcule sur vos derniers réglages.') : apercu.totals.changed === 0 ? t('Aucun prix ne changerait avec ces réglages.') : undefined}
            onClick={() => { void appliquer(); }}
          >
            {ecriture ? t('Écriture…') : apercu ? t('Appliquer à {n} prix', { n: apercu.totals.changed }) : t('Appliquer')}
          </button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {/* ── Sur quels articles ? ── */}
        <section>
          <h3 style={{ margin: '0 0 8px', fontSize: 'var(--t-dense)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
            
            {t('Sur quels articles ?')}
          </h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)' }}>
              {MASS_TARGETS.map((k) => (
                <button key={k} type="button" style={segment(target === k)} onClick={() => { setCible(k); setValeur(''); }}>
                  {massTargetLabel(k)}
                </button>
              ))}
            </div>
            <div style={{ minWidth: 240, flex: 1 }}>
              <Picker
                id="bo-mass-price-value"
                variant="field-inline"
                label={t('Valeur ciblée')}
                allLabel={t('À choisir')}
                options={valeur ? [{ value: valeur, label: valeur }] : []}
                onSearch={(term) => api.articleFacetSearch(target, term).then((r) => r.map((o) => ({ value: o.value, label: o.value })))}
                value={valeur}
                onChange={setValeur}
              />
            </div>
          </div>
        </section>

        {/* ── Quelle variation ? ── */}
        <section>
          <h3 style={{ margin: '0 0 8px', fontSize: 'var(--t-dense)', fontWeight: 700, letterSpacing: '.05em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
            
            {t('Quelle variation ?')}
          </h3>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', gap: 2, padding: 3, borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', border: '1px solid var(--ink-200)' }}>
              <button aria-pressed={mode === 'pct'} type="button" style={segment(mode === 'pct')} onClick={() => setMode('pct')}>%</button>
              <button aria-pressed={mode === 'eur'} type="button" style={segment(mode === 'eur')} onClick={() => setMode('eur')}>{currentCurrency().symbol}</button>
            </div>
            <input
              inputMode="decimal"
              value={saisie}
              onChange={(e) => setSaisie(e.target.value)}
              placeholder={t('Valeur — négative pour baisser')}
              style={{
                height: 36, padding: '0 12px', width: 220, borderRadius: 'var(--r-ctrl)',
                border: `1px solid ${saisie && !change ? 'var(--rose)' : 'var(--ink-200)'}`,
                fontFamily: 'var(--font-mono)', fontSize: 'var(--t-lead)', textAlign: 'right', background: 'var(--paper)',
              }}
            />
            <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)' }}>{t('Négative pour baisser.')}</span>
          </div>
          <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginTop: 10 }}>
            <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', alignSelf: 'center' }}>{t('Arrondi psychologique :')}</span>
            {ARRONDIS.map((a) => (
              <button aria-pressed={arrondi === a.k}
                key={a.k} type="button" title={typeof a.exemple === 'number' ? euro(a.exemple) : t(a.exemple)}
                onClick={() => setArrondi(a.k)}
                style={{
                  height: 30, padding: '0 11px', borderRadius: 'var(--r-ctrl)', cursor: 'pointer',
                  border: `1px solid ${arrondi === a.k ? 'var(--teal-500)' : 'var(--ink-200)'}`,
                  background: arrondi === a.k ? 'var(--teal-100)' : 'var(--paper)',
                  color: arrondi === a.k ? 'var(--teal-800)' : 'var(--ink-700)', fontSize: 'var(--t-body)', fontWeight: 600,
                }}
              >{t(a.l)}</button>
            ))}
          </div>
        </section>

        {/* ── L'aperçu ── */}
        <section>
          {failure !== null ? (
            <p role="alert" style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--danger-ink)' }}>
              {t('L’aperçu n’a pas pu être calculé : {v}', { v: failure })}
            </p>
          ) : !filters || !change ? (
            <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>
              
              {t('L’aperçu apparaîtra ici — choisissez une cible et une variation.')}
            </p>
          ) : apercu === null ? (
            <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{t('Calcul de l’aperçu…')}</p>
          ) : apercu.totals.variants === 0 ? (
            <p style={{ margin: 0, fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>
              
              {t('Aucun article dans cette sélection — choisissez une autre cible.')}
            </p>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 22, marginBottom: 10, flexWrap: 'wrap', opacity: enCours ? 0.6 : 1 }}>
                <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>
                  {apercu.totals.variants !== apercu.totals.changed
                    ? <Trans k='Articles concernés : <1>{v}</1> ({changent}, {memePrix})'
                        vars={{
                          v: apercu.totals.models,
                          changent: t('{n} prix changent', { n: apercu.totals.changed }),
                          memePrix: t('{n} retombent au même prix', { n: apercu.totals.variants - apercu.totals.changed }),
                        }}
                        parts={[(x) => <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} />
                    : <Trans k='Articles concernés : <1>{v}</1> ({changent})'
                        vars={{ v: apercu.totals.models, changent: t('{n} prix changent', { n: apercu.totals.changed }) }}
                        parts={[(x) => <strong style={{ color: 'var(--ink-900)', fontVariantNumeric: 'tabular-nums' }}>{x}</strong>]} />}
                </span>
                {apercu.totals.avgDeltaBp != null && (
                  <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-600)' }}>
                    
                    {t('Variation moyenne :')} <strong style={{ color: apercu.totals.avgDeltaBp >= 0 ? 'var(--teal-800)' : 'var(--rose-fg)', fontVariantNumeric: 'tabular-nums' }}>
                      {apercu.totals.avgDeltaBp >= 0 ? '+' : '−'}{formatMinor(Math.abs(apercu.totals.avgDeltaBp), { scale: 2, displayScale: 1 })} %
                    </strong>
                  </span>
                )}
              </div>

              <div style={{ border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden', opacity: enCours ? 0.6 : 1 }}>
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: 620 }}>
                    <div style={{
                      display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) 120px 120px 92px 70px', gap: 9,
                      padding: '9px 14px', borderBottom: '1px solid var(--ink-200)', background: 'var(--ink-50)',
                      fontSize: 'var(--t-sub)', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase', color: 'var(--ink-500)',
                    }}>
                      <span>{t('Article')}</span>
                      <span style={{ textAlign: 'right' }}>{t('Actuel')}</span>
                      <span style={{ textAlign: 'right' }}>{t('Nouveau')}</span>
                      <span style={{ textAlign: 'right' }}>{t('Écart')}</span>
                      <span style={{ textAlign: 'right' }}>{t('Marge')}</span>
                    </div>
                    {apercu.rows.map((r) => {
                      // L'écart n'est UN nombre que si le modèle a un prix : sur une plage, il se lit
                      // dans les deux colonnes — un delta unique serait un chiffre inventé.
                      const deltaUnique = r.curMinCents === r.curMaxCents && r.nextMinCents === r.nextMaxCents
                        ? r.nextMinCents - r.curMinCents : null;
                      return (
                        <div key={r.modelKey} style={{
                          display: 'grid', gridTemplateColumns: 'minmax(0,1.5fr) 120px 120px 92px 70px', gap: 9,
                          padding: '8px 14px', alignItems: 'baseline', borderBottom: '1px solid var(--ink-100)',
                        }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</div>
                            <div style={{ fontSize: 'var(--t-sub)', color: 'var(--ink-500)', fontFamily: 'var(--font-mono)' }}>
                              {r.code}{r.variants > 1 ? ' · ' + t('{n} déclinaison', { n: r.variants }) : ''}
                            </div>
                          </div>
                          <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: 'var(--ink-600)' }}>{plage(r.curMinCents, r.curMaxCents)}</span>
                          <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', fontWeight: 700, color: 'var(--ink-900)' }}>{plage(r.nextMinCents, r.nextMaxCents)}</span>
                          <span style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-note)', color: deltaUnique == null ? 'var(--ink-400)' : deltaUnique > 0 ? 'var(--teal-800)' : deltaUnique < 0 ? 'var(--rose-fg)' : 'var(--ink-400)' }}>
                            {deltaUnique == null ? '—' : `${deltaUnique >= 0 ? '+' : '−'}${euro(Math.abs(deltaUnique))}`}
                          </span>
                          <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: 'var(--t-note)', color: 'var(--ink-700)' }}>{margeApres(r)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {apercu.more > 0 && (
                  <div style={{ padding: '8px 14px', background: 'var(--ink-50)', fontSize: 'var(--t-note)', color: 'var(--ink-500)' }}>
                    {t('+ {n} article non affiché — tous seront modifiés.', { n: apercu.more })}
                  </div>
                )}
              </div>
            </>
          )}
        </section>

        <p style={{ margin: 0, fontSize: 'var(--t-note)', color: 'var(--ink-500)', display: 'flex', gap: 7, alignItems: 'center' }}>
          <Icon name="info" size={13} />
          
          {t('Chaque déclinaison part de SON prix : les dérogations posées à la fiche bougent du même pas, elles ne sont jamais aplaties.')}
        </p>
      </div>
    </Modal>
  );
}
